import { useCallback, useEffect, useRef, useState } from 'react';

import { downloadQuotePdf, isAbortError } from '../../utils/quotes/quotes';
import { openOrDownloadPdf, type OpenPdfOutcome } from '../../utils/utils';

/** `'aborted'` means the caller cancelled/unmounted mid-flight and should stay silent. */
export type DownloadAndOpenOutcome = OpenPdfOutcome | 'aborted';

export const useDownloadQuotePdf = () => {
  const [downloadingQuoteId, setDownloadingQuoteId] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const currentQuoteIdRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setDownloadingQuoteId(null);
    currentQuoteIdRef.current = null;
  }, []);

  const downloadPdf = useCallback(async (brokerId: string, prospectId: number, quoteId: number) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    currentQuoteIdRef.current = quoteId;
    setDownloadingQuoteId(quoteId);

    try {
      return await downloadQuotePdf(brokerId, prospectId, quoteId, { signal: controller.signal });
    } finally {
      if (currentQuoteIdRef.current === quoteId) {
        setDownloadingQuoteId(null);
        currentQuoteIdRef.current = null;
      }
    }
  }, []);

  /** Polls for the PDF and then surfaces it (new tab → download fallback → failure).
   *  This is the single entry point both callers should use so the open/fallback
   *  logic stays in one place. Returns `'aborted'` when cancelled mid-flight. */
  const downloadAndOpenPdf = useCallback(
    async (brokerId: string, prospectId: number, quoteId: number): Promise<DownloadAndOpenOutcome> => {
      try {
        const { documentUrl } = await downloadPdf(brokerId, prospectId, quoteId);
        return await openOrDownloadPdf(documentUrl);
      } catch (err) {
        if (isAbortError(err)) return 'aborted';
        return 'failed';
      }
    },
    [downloadPdf]
  );

  return { downloadPdf, downloadAndOpenPdf, downloadingQuoteId, cancel };
};
